import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import { Seo } from '@/components/Seo';
import SharedFooter from '@/components/feature/SharedFooter';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import FaqV2 from '@/pages/info/v2/FaqV2';

interface FaqItem {
  qKey: string;
  aKey: string;
}

interface FaqGroup {
  titleKey: string;
  items: FaqItem[];
}

export default function FaqPage() {
  const { isNew } = useSiteDesign();
  return isNew ? <FaqV2 /> : <ClassicFaqPage />;
}

function ClassicFaqPage() {
  const { t } = useTranslation();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const groups: FaqGroup[] = [
    {
      titleKey: 'faq.groupA.title',
      items: [
        { qKey: 'faq.groupA.q1.question', aKey: 'faq.groupA.q1.answer' },
        { qKey: 'faq.groupA.q2.question', aKey: 'faq.groupA.q2.answer' },
        { qKey: 'faq.groupA.q3.question', aKey: 'faq.groupA.q3.answer' },
        { qKey: 'faq.groupA.q4.question', aKey: 'faq.groupA.q4.answer' },
        { qKey: 'faq.groupA.q5.question', aKey: 'faq.groupA.q5.answer' },
      ],
    },
    {
      titleKey: 'faq.groupB.title',
      items: [
        { qKey: 'faq.groupB.q1.question', aKey: 'faq.groupB.q1.answer' },
        { qKey: 'faq.groupB.q2.question', aKey: 'faq.groupB.q2.answer' },
        { qKey: 'faq.groupB.q3.question', aKey: 'faq.groupB.q3.answer' },
      ],
    },
    {
      titleKey: 'faq.groupC.title',
      items: [
        { qKey: 'faq.groupC.q1.question', aKey: 'faq.groupC.q1.answer' },
        { qKey: 'faq.groupC.q2.question', aKey: 'faq.groupC.q2.answer' },
        { qKey: 'faq.groupC.q3.question', aKey: 'faq.groupC.q3.answer' },
      ],
    },
    {
      titleKey: 'faq.groupD.title',
      items: [
        { qKey: 'faq.groupD.q1.question', aKey: 'faq.groupD.q1.answer' },
        { qKey: 'faq.groupD.q2.question', aKey: 'faq.groupD.q2.answer' },
        { qKey: 'faq.groupD.q3.question', aKey: 'faq.groupD.q3.answer' },
      ],
    },
  ];

  const toggle = (key: string) => {
    setOpenKey(openKey === key ? null : key);
  };

  // Build FAQPage schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: groups.flatMap((g, gi) =>
      g.items.map((item, ii) => ({
        '@type': 'Question',
        name: t(item.qKey),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t(item.aKey),
        },
      }))
    ),
  };

  return (
    <>
      <Seo pageKey="faq" jsonLd={faqSchema} />

      <main className="w-full min-h-screen bg-white font-sans">
        <SharedNavbar />

        {/* ===== HERO ===== */}
        <section className="w-full bg-[#1A1A1A] pt-28 pb-16 md:pt-32 md:pb-20 px-6 md:px-12 lg:px-20">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-6 h-px bg-[#7B1113]" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                {t('faq.hero.label')}
              </span>
              <div className="w-6 h-px bg-[#7B1113]" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.15] mb-4">
              {t('faq.hero.heading')}
            </h1>
            <p className="text-sm text-white/40 font-light max-w-lg mx-auto">
              {t('faq.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* ===== ACCORDION GROUPS ===== */}
        <section className="w-full bg-white py-16 md:py-24 px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl mx-auto flex flex-col gap-12">
            {groups.map((group, gi) => (
              <div key={gi}>
                <h2 className="font-serif text-xl md:text-2xl font-bold text-[#2A2A2A] mb-5 pb-3 border-b border-[#E8E8E6]">
                  {t(group.titleKey)}
                </h2>
                <div className="flex flex-col gap-3">
                  {group.items.map((item, ii) => {
                    const key = `${gi}-${ii}`;
                    const isOpen = openKey === key;
                    return (
                      <div
                        key={key}
                        className="border border-[#E8E8E6] rounded-2xl overflow-hidden transition-colors"
                        itemScope
                        itemProp="mainEntity"
                        itemType="https://schema.org/Question"
                      >
                        <button
                          onClick={() => toggle(key)}
                          className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                        >
                          <span
                            className="font-serif text-base md:text-lg font-semibold text-[#2A2A2A]"
                            itemProp="name"
                          >
                            {t(item.qKey)}
                          </span>
                          <i
                            className={`ri-arrow-down-s-line text-[#8A8A8A] text-lg shrink-0 transition-transform duration-300 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <div
                          className={`overflow-hidden transition-all duration-300 ${
                            isOpen ? 'max-h-[600px]' : 'max-h-0'
                          }`}
                          itemScope
                          itemProp="acceptedAnswer"
                          itemType="https://schema.org/Answer"
                        >
                          <div className="px-6 pb-5">
                            <p
                              className="text-sm text-[#5A5A5A] font-light leading-[1.9]"
                              itemProp="text"
                            >
                              {t(item.aKey)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="max-w-3xl mx-auto mt-16 p-8 bg-[#F0EFED] rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex flex-col gap-2">
              <h3 className="font-serif text-xl font-bold text-[#2A2A2A]">
                {t('faq.bottom.title')}
              </h3>
              <p className="text-sm text-[#8A8A8A] font-light">
                {t('faq.bottom.body')}
              </p>
            </div>
            <a
              href="/contact"
              className="w-full sm:w-auto px-6 py-3 bg-[#7B1113] text-white text-xs tracking-[0.12em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center whitespace-nowrap"
            >
              {t('faq.bottom.cta')}
            </a>
          </div>
        </section>

        <SharedFooter />
      </main>
    </>
  );
}